import { Link, useParams } from "react-router-dom";

import styles from './LoginPage.module.css'
import { useEffect, useState } from "react";
import authService from "../services/authService";

interface PasswordErrors {
    newPassword?: string;
    confirmPassword?: string;
}


const ResetPasswordPage = () => {

    const { token } = useParams<{ token: string }>();
    const [error, setError] = useState('');
    const [isTokenValid, setIsTokenValid] = useState(false);
    const [passwordChanged, setPasswordChanged] = useState(false);
    const [success, setSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordErrors, setPasswordErrors] = useState<PasswordErrors>({});

    const validateToken = async () => {
        if (!token) {
            setError('Invalid or missing token.');
            return false;
        }

        try {
            // Call the service to validate the token
            const result = await authService.validateForgotPasswordToken(token);
            if (result === "Valid") {
                return true;
            } else if (result === "Invalid") {
                setError('The password reset token is invalid.');
                return false;
            } else if (result === "Expired") {
                setError('The password reset token has expired.');
                return false;
            }

        } catch (error) {
            setError('An error occurred while validating the token. Please try again later.');
            return false;
        }
        return false;
    }

    useEffect(() => {
        const checkToken = async () => {
            const valid = await validateToken();
            setIsTokenValid(valid);
        };

        checkToken();
    }, [token]);


    const validatePasswordChange = () => {
        let errors: PasswordErrors = {};

        if (!newPassword) {
            errors.newPassword = "New Password is required";
        } else if (newPassword.length < 6) {
            errors.newPassword = "New Password must be at least 6 characters long";
        }
        if (!confirmPassword) {
            errors.confirmPassword = "Confirm Password is required";
        } else if (confirmPassword.length < 6) {
            errors.confirmPassword = "Confirm Password must be at least 6 characters long";
        }
        if (!errors.newPassword && !errors.confirmPassword) {

            if (newPassword !== confirmPassword) {
                errors.confirmPassword = "New Password and Confirm Password do not match";
            }
        }
        setPasswordErrors(errors);
        return Object.keys(errors).length === 0;
    }

    const handleChangePassword = () => {
        const changePassword = async () => {
            try {
                // Call the service to reset the password using the token
                const result = await authService.resetPassword(token || '', newPassword);
                if (result === "Success") {
                    setPasswordChanged(true);
                    setSuccess('Your password has been reset successfully. You can now log in with your new password.');
                }
                else if (result === "InvalidToken") {
                    setError('The password reset token is invalid.');
                } else if (result === "ExpiredToken") {
                    setError('The password reset token has expired.');
                }
                else if (result === "PasswordTooShort") {
                    setError('The new password must be at least 6 characters long.');
                }
                else if (result === "Error") {
                    setError('An error occurred while resetting your password. Please try again later.');
                }
                else {
                    setError('An unknown error occurred while resetting your password. Please try again later.');
                }
            } catch (error) {
                setError('An error occurred while resetting your password. Please try again later.');
            }

            setIsSubmitting(false);
        }

        if (validatePasswordChange()) {
            setIsSubmitting(true);
            changePassword();
        }
    }

    return (
        <>
            <div className='d-flex justify-content-lg-center'>
                <Link to="/"><img className={styles.loginLogo} src="/images/tahpi-plus.png" alt=""></img></Link>
            </div>
            <h3 className={`mb-4 text-center ${styles.title}`}>Reset Your Password</h3>
            {!passwordChanged && isTokenValid && (
                <>
                    <div className={`${styles.formFloatingZindex} form-floating mb-3`}>
                        <input type="password" className={`form-control ${passwordErrors.newPassword ? ` is-invalid` : ``}`} placeholder="New Password" value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        ></input>
                        <label>New Password</label>
                        {passwordErrors.newPassword && <div className="invalid-feedback">{passwordErrors.newPassword}</div>}
                    </div>
                    <div className={`${styles.formFloatingZindex} form-floating mb-3`}>
                        <input type="password" className={`form-control ${passwordErrors.confirmPassword ? ` is-invalid` : ``}`} placeholder="Confirm Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}></input>
                        <label>Confirm Password</label>
                        {passwordErrors.confirmPassword && <div className="invalid-feedback">{passwordErrors.confirmPassword}</div>}
                    </div>

                    <button className="btn btn-lg btn-dark w-100 py-2 my-3 rounded-0" onClick={handleChangePassword} disabled={isSubmitting}>
                        {isSubmitting ? (<><span className={`spinner-border ${styles.formSigninSpinner}`} role="status" aria-hidden="true"></span> Changing password ...</>) : ("Change Password")}
                    </button>
                </>
            )}
            {success && <div className="alert alert-success mt-2" role="alert">{success}</div>}
            {error && <div className="alert alert-danger mt-2" role="alert">{error}</div>}
            {!isTokenValid && (
                <Link className="btn btn-lg btn-outline-dark w-100 py-2 mt-3 rounded-0" to="/forgotPassword">Forgot Password</Link>
            )}
            {passwordChanged && (
                <Link className="btn btn-lg btn-outline-dark w-100 py-2 mt-3 rounded-0" to="/login">Login with New Password</Link>
            )}

        </>
    );
}
export default ResetPasswordPage;